import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import axios from 'axios';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, Volume2, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WebRTC configuration - fetched from backend
const defaultRtcConfig = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun.cloudflare.com:3478' }
  ],
  iceCandidatePoolSize: 10
};

const VideoCall = () => {
  const navigate = useNavigate();
  const { callId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { connected, addMessageHandler, callSignals, clearCallSignals } = useWebSocket();
  
  const [callStatus, setCallStatus] = useState('connecting');
  const [rtcConfig, setRtcConfig] = useState(defaultRtcConfig);

  const rtcConfigRef = useRef(defaultRtcConfig);

  // Fetch TURN credentials from backend on mount (also stored in ref for sync access)
  useEffect(() => {
    const fetchTurnCredentials = async () => {
      try {
        const res = await axios.get(`${API}/calls/turn-credentials`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.iceServers) {
          const cfg = { iceServers: res.data.iceServers, iceCandidatePoolSize: 10 };
          rtcConfigRef.current = cfg;
          setRtcConfig(cfg);
          console.log('[CALL] TURN credentials fetched from backend');
        }
      } catch (e) {
        console.warn('[CALL] Using default TURN config:', e.message);
      }
    };
    if (token) fetchTurnCredentials();
  }, [token]);

  // Blocking helper: await TURN creds before creating PC (with 3s cap)
  const ensureTurnCreds = useCallback(async () => {
    // If already fetched (non-default), return immediately
    if (rtcConfigRef.current !== defaultRtcConfig) return rtcConfigRef.current;
    try {
      const res = await axios.get(`${API}/calls/turn-credentials`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 3000
      });
      if (res.data?.iceServers) {
        const cfg = { iceServers: res.data.iceServers, iceCandidatePoolSize: 10 };
        rtcConfigRef.current = cfg;
        setRtcConfig(cfg);
        console.log('[CALL] TURN credentials fetched (sync)');
        return cfg;
      }
    } catch (e) {
      console.warn('[CALL] TURN fetch failed, using default:', e.message);
    }
    return rtcConfigRef.current;
  }, [token]);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [remoteUser, setRemoteUser] = useState(null);
  const [currentCallType, setCurrentCallType] = useState(searchParams.get('type') || 'video');
  const [showAddPeople, setShowAddPeople] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [isIncoming] = useState(searchParams.get('incoming') === 'true');
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  // Callback ref to attach local stream as soon as the video element mounts
  const localVideoCallbackRef = useCallback((node) => {
    localVideoRef.current = node;
    if (node && localStreamRef.current) {
      node.srcObject = localStreamRef.current;
      node.play().catch(() => {});
    }
  }, []);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const toUserIdRef = useRef(searchParams.get('userId'));
  const pendingCandidatesRef = useRef([]);
  const connectionTimeoutRef = useRef(null);
  const callerPollRef = useRef(null);

  // Keep a ref to always access latest callSignals without stale closures
  const callSignalsRef = useRef(callSignals);
  useEffect(() => { callSignalsRef.current = callSignals; }, [callSignals]);
  useEffect(() => {
    const fetchRemoteUser = async () => {
      const userId = toUserIdRef.current;
      if (userId) {
        try {
          const response = await axios.get(`${API}/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setRemoteUser(response.data);
        } catch (error) {
          console.error('Failed to fetch remote user:', error);
        }
      }
    };
    fetchRemoteUser();
  }, [token]);

  const cleanup = useCallback(() => {
    if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
    if (callerPollRef.current) { clearInterval(callerPollRef.current); callerPollRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    pendingCandidatesRef.current = [];
    clearCallSignals();
  }, [clearCallSignals]);

  const sendSignal = useCallback(async (signalType, signalData) => {
    try {
      await axios.post(`${API}/calls/signal`, {
        call_id: callId,
        to_user_id: toUserIdRef.current,
        signal_type: signalType,
        signal_data: signalData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error sending signal:', error);
    }
  }, [callId, token]);

  const createPeerConnection = useCallback(async () => {
    const cfg = await ensureTurnCreds();
    const pc = new RTCPeerConnection(cfg);
    peerConnectionRef.current = pc;

    // Add local stream tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Handle incoming stream
    pc.ontrack = (event) => {
      console.log('[Call] Remote track received:', event.track.kind);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
        // Force play on mobile (autoplay may be blocked)
        remoteVideoRef.current.play().catch(() => {});
      }
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        sendSignal('ice-candidate', { candidate: event.candidate });
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[Call] ICE state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setCallStatus('connected');
        if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
        if (callerPollRef.current) { clearInterval(callerPollRef.current); callerPollRef.current = null; }
      } else if (pc.iceConnectionState === 'failed') {
        console.log('[Call] ICE failed, attempting restart');
        pc.restartIce();
      } else if (pc.iceConnectionState === 'disconnected') {
        // Give it a few seconds to recover before ending
        setTimeout(() => {
          if (peerConnectionRef.current?.iceConnectionState === 'disconnected') {
            endCall();
          }
        }, 5000);
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('[Call] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallStatus('connected');
        if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
      } else if (pc.connectionState === 'failed') {
        toast.error('Connection failed');
        endCall();
      }
    };

    // Process any pending ICE candidates
    if (pendingCandidatesRef.current.length > 0) {
      for (const candidate of pendingCandidatesRef.current) {
        try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) { console.warn('[Call] Pending ICE error:', e); }
      }
      pendingCandidatesRef.current = [];
    }

    return pc;
  }, [sendSignal, ensureTurnCreds]);

  const handleSignal = useCallback(async (data) => {
    let pc = peerConnectionRef.current;
    console.log(`[CALL] handleSignal: type=${data.signal_type}, pc exists=${!!pc}, pc state=${pc?.signalingState}`);
    if (!pc) {
      console.log(`[CALL] No peer connection in handleSignal, creating one`);
      pc = await createPeerConnection();
    }

    try {
      if (data.signal_type === 'offer') {
        if (pc.signalingState === 'stable' || pc.signalingState === 'have-local-offer') {
          console.log(`[CALL] Processing OFFER, current signalingState: ${pc.signalingState}`);
          await pc.setRemoteDescription(new RTCSessionDescription(data.signal_data.sdp));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await sendSignal('answer', { sdp: answer });
          console.log(`[CALL] Answer sent`);
        } else {
          console.log(`[CALL] Ignoring OFFER in state: ${pc.signalingState}`);
        }
      } else if (data.signal_type === 'answer') {
        if (pc.signalingState === 'have-local-offer') {
          console.log(`[CALL] Processing ANSWER`);
          await pc.setRemoteDescription(new RTCSessionDescription(data.signal_data.sdp));
        } else {
          console.log(`[CALL] Ignoring duplicate ANSWER, already in state: ${pc.signalingState}`);
        }
      } else if (data.signal_type === 'ice-candidate' && data.signal_data.candidate) {
        if (!pc.remoteDescription) {
          pendingCandidatesRef.current.push(data.signal_data.candidate);
          console.log(`[CALL] Queued ICE candidate (no remote desc yet)`);
        } else {
          await pc.addIceCandidate(new RTCIceCandidate(data.signal_data.candidate));
        }
      }

      if ((data.signal_type === 'offer' || data.signal_type === 'answer') && pendingCandidatesRef.current.length > 0) {
        console.log(`[CALL] Flushing ${pendingCandidatesRef.current.length} pending ICE candidates`);
        for (const candidate of pendingCandidatesRef.current) {
          try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) { console.warn('[Call] Queued ICE error:', e); }
        }
        pendingCandidatesRef.current = [];
      }
    } catch (error) {
      console.error('[CALL] Signal error:', error);
    }
  }, [createPeerConnection, sendSignal]);

  const initializeCall = useCallback(async () => {
    try {
      console.log(`[CALL] initializeCall started - isIncoming: ${isIncoming}, callId: ${callId}`);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: currentCallType === 'video' ? { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } : false,
        audio: true
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      console.log(`[CALL] Got local media stream`);

      const pc = await createPeerConnection();
      console.log(`[CALL] Peer connection created, state: ${pc.signalingState}`);
      
      if (!isIncoming) {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await sendSignal('offer', { sdp: offer });
        setCallStatus('ringing');
        console.log(`[CALL] Outgoing: offer sent, status = ringing`);

        // SAFETY NET: Poll for missed signals (answer / remote ICE) in case the
        // callee's WS delivery to us failed (WS reconnect, cross-container, etc).
        // Backend persists every signal in db.call_signals — we replay any missed.
        callerPollRef.current = setInterval(async () => {
          try {
            const pc2 = peerConnectionRef.current;
            if (!pc2 || pc2.iceConnectionState === 'connected' || pc2.iceConnectionState === 'completed') {
              clearInterval(callerPollRef.current);
              callerPollRef.current = null;
              return;
            }
            const res = await axios.get(`${API}/calls/${callId}/pending-signals`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const pending = res.data?.signals || [];
            if (pending.length > 0) {
              console.log(`[CALL] Caller polled ${pending.length} missed signals`);
              for (const s of pending) {
                await handleSignal({
                  call_id: s.call_id,
                  signal_type: s.signal_type,
                  signal_data: s.signal_data,
                  from_user_id: s.from_user_id
                });
              }
            }
          } catch (e) {
            console.warn('[CALL] Caller poll error:', e.message);
          }
        }, 2000);
      } else {
        console.log(`[CALL] Incoming: peer connection ready, waiting for offer signal. callSignals queued: ${callSignalsRef.current.length}`);
        // Process any already-queued signals
        const queued = callSignalsRef.current.filter(s => s.call_id === callId);
        if (queued.length > 0) {
          console.log(`[CALL] Processing ${queued.length} queued signals immediately`);
          for (const signal of queued) {
            await handleSignal(signal);
          }
          clearCallSignals();
        }
      }

      // Connection timeout - 30 seconds
      connectionTimeoutRef.current = setTimeout(() => {
        if (peerConnectionRef.current?.connectionState !== 'connected' && 
            peerConnectionRef.current?.iceConnectionState !== 'connected') {
          toast.error('Connection timed out');
          endCall();
        }
      }, 30000);
    } catch (error) {
      console.error('Error initializing call:', error);
      toast.error('Failed to access camera/microphone');
      navigate(-1);
    }
  }, [currentCallType, createPeerConnection, isIncoming, navigate, sendSignal]);

  // Handle WebSocket messages
  useEffect(() => {
    if (!connected) return;

    const handleMessage = (data) => {
      if (data.call_id !== callId) return;
      
      switch (data.type) {
        case 'call_signal':
          // Process signals immediately if peer connection is ready, otherwise let useEffect handle via callSignals queue
          if (peerConnectionRef.current) {
            console.log(`[CALL] WebSocket signal received - type: ${data.signal_type}, pc state: ${peerConnectionRef.current?.signalingState}`);
            handleSignal(data);
            clearCallSignals();
          } else {
            console.log(`[CALL] WebSocket signal received but no peer connection yet, queuing: ${data.signal_type}`);
          }
          break;
        case 'call_answered':
          setCallStatus('connecting');
          break;
        case 'call_rejected':
          toast.error('Call rejected');
          cleanup();
          navigate(-1);
          break;
        case 'call_ended':
          toast.info('Call ended');
          cleanup();
          navigate(-1);
          break;
        default:
          break;
      }
    };

    const removeHandler = addMessageHandler(handleMessage);
    return () => removeHandler();
  }, [connected, callId, addMessageHandler, handleSignal, cleanup, navigate]);

  // Process queued call signals for both outgoing and incoming calls
  useEffect(() => {
    if (localStreamRef.current && peerConnectionRef.current && callSignals.length > 0) {
      callSignals.forEach(signal => {
        if (signal.call_id === callId) {
          handleSignal(signal);
        }
      });
      clearCallSignals();
    }
  }, [callSignals, callId, handleSignal, clearCallSignals]);

  // Re-attach local stream when call status changes (ensures attachment after UI transitions)
  // Uses polling for WebView compatibility where callback refs may fire before DOM is painted
  useEffect(() => {
    if (!localStreamRef.current) return;
    
    const attachStream = () => {
      if (localVideoRef.current && localStreamRef.current) {
        if (localVideoRef.current.srcObject !== localStreamRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }
        localVideoRef.current.play().catch(() => {});
        return true;
      }
      return false;
    };
    
    // Try immediately
    if (attachStream()) return;
    
    // Retry with increasing delays for WebView compatibility
    const timers = [
      setTimeout(attachStream, 100),
      setTimeout(attachStream, 300),
      setTimeout(attachStream, 600),
      setTimeout(attachStream, 1000),
      setTimeout(attachStream, 2000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [callStatus]);

  // Initialize call on mount
  useEffect(() => {
    // Both incoming and outgoing use initializeCall()
    // For incoming: IncomingCallModal already called /answer before navigating here
    // initializeCall() will set up peer connection and wait for offer (no offer created when isIncoming)
    initializeCall();

    // End call when leaving the page (close tab, navigate away)
    const handleBeforeUnload = () => {
      navigator.sendBeacon(`${API}/calls/${callId}/end`);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanup();
      // Notify the other party when component unmounts
      axios.post(`${API}/calls/${callId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    };
  }, []);

  const answerCall = async () => {
    try {
      // Get local media FIRST before processing signals
      const stream = await navigator.mediaDevices.getUserMedia({
        video: currentCallType === 'video' ? { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } : false,
        audio: true
      });
      localStreamRef.current = stream;
      console.log('[Call] Got local stream, video tracks:', stream.getVideoTracks().length);
      
      // Create peer connection with local tracks FIRST
      const pc = await createPeerConnection();
      
      // Now notify the backend we answered (peer connection is ready to receive signals)
      await axios.post(`${API}/calls/${callId}/answer`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCallStatus('connecting');
      
      // Fetch any signals (offer, ice-candidates) that were sent BEFORE our WS connected.
      // Without this, if the caller's offer arrives while we're still loading, it's lost
      // and the call gets stuck at "Connecting..." forever.
      try {
        const pendingRes = await axios.get(`${API}/calls/${callId}/pending-signals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const pending = pendingRes.data?.signals || [];
        console.log(`[Call] Replaying ${pending.length} pending signals from server`);
        for (const s of pending) {
          await handleSignal({
            call_id: s.call_id,
            signal_type: s.signal_type,
            signal_data: s.signal_data,
            from_user_id: s.from_user_id
          });
        }
      } catch (e) {
        console.warn('[Call] Failed to fetch pending signals:', e.message);
      }
      
      // Process any queued signals using the ref (avoids stale closure)
      const currentSignals = callSignalsRef.current;
      if (currentSignals.length > 0) {
        for (const signal of currentSignals) {
          if (signal.call_id === callId) {
            await handleSignal(signal);
          }
        }
        clearCallSignals();
      }

      // Connection timeout - 30 seconds
      connectionTimeoutRef.current = setTimeout(() => {
        if (peerConnectionRef.current?.connectionState !== 'connected' && 
            peerConnectionRef.current?.iceConnectionState !== 'connected') {
          toast.error('Connection timed out');
          endCall();
        }
      }, 30000);
    } catch (error) {
      console.error('Failed to answer call:', error);
      toast.error('Failed to access camera/microphone');
      navigate(-1);
    }
  };

  const rejectCall = async () => {
    try {
      await axios.post(`${API}/calls/${callId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      cleanup();
      navigate(-1);
    } catch (error) {
      toast.error('Failed to reject call');
      navigate(-1);
    }
  };

  const endCall = async () => {
    // Always cleanup and navigate immediately - don't wait for API
    cleanup();
    navigate(-1);
    try {
      await axios.post(`${API}/calls/${callId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error ending call:', error);
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  };

  // Switch from audio-only call to video call
  const switchToVideo = async () => {
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
      const videoTrack = videoStream.getVideoTracks()[0];
      
      // Add video track to local stream
      if (localStreamRef.current) {
        localStreamRef.current.addTrack(videoTrack);
      }
      
      // Add video track to peer connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.addTrack(videoTrack, localStreamRef.current);
        // Renegotiate
        const offer = await peerConnectionRef.current.createOffer();
        await peerConnectionRef.current.setLocalDescription(offer);
        await sendSignal('offer', { sdp: offer });
      }
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
      
      setCurrentCallType('video');
      setIsVideoEnabled(true);
      toast.success('Switched to video call');
    } catch (error) {
      console.error('Failed to switch to video:', error);
      toast.error('Failed to enable camera');
    }
  };

  // Switch speaker on/off (for audio calls - routes audio to speakerphone)
  const toggleSpeaker = () => {
    if (remoteVideoRef.current) {
      // Use setSinkId if available (Chrome/Edge) to switch audio output
      if (typeof remoteVideoRef.current.setSinkId === 'function') {
        // Toggle between default (earpiece) and speaker
        const newSinkId = isSpeakerOn ? '' : 'default';
        remoteVideoRef.current.setSinkId(newSinkId).then(() => {
          setIsSpeakerOn(!isSpeakerOn);
        }).catch(() => {
          // Fallback: just toggle the state for visual feedback
          setIsSpeakerOn(!isSpeakerOn);
        });
      } else {
        // For browsers that don't support setSinkId, toggle volume as a hint
        remoteVideoRef.current.volume = isSpeakerOn ? 0.3 : 1.0;
        setIsSpeakerOn(!isSpeakerOn);
      }
    } else {
      setIsSpeakerOn(!isSpeakerOn);
    }
  };

  // Incoming call UI
  if (callStatus === 'ringing' && isIncoming) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 to-black flex flex-col items-center justify-center z-50" data-testid="incoming-call-screen">
        <div className="text-center">
          {remoteUser?.avatar_url ? (
            <img 
              src={remoteUser.avatar_url.startsWith('http') ? remoteUser.avatar_url : `${BACKEND_URL}${remoteUser.avatar_url}`}
              alt={remoteUser.display_name}
              className="w-32 h-32 rounded-full object-cover mx-auto mb-6 border-4 border-[#D4AF37]/30 shadow-2xl animate-pulse"
              data-testid="incoming-call-avatar"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium text-5xl mx-auto mb-6 animate-pulse" data-testid="incoming-call-avatar">
              {remoteUser?.display_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <h2 className="text-white text-2xl font-medium mb-2">
            {remoteUser?.display_name || 'Unknown'}
          </h2>
          <p className="text-gray-400 mb-12">
            Incoming {currentCallType} call...
          </p>
          
          <div className="flex items-center justify-center gap-8">
            <button
              data-testid="reject-call-btn"
              onClick={rejectCall}
              className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors"
            >
              <PhoneOff size={28} className="text-white" />
            </button>
            <button
              data-testid="answer-call-btn"
              onClick={answerCall}
              className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center hover:bg-green-600 transition-colors animate-bounce"
            >
              <Phone size={28} className="text-white" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 flex flex-col z-50" data-testid="video-call-screen" style={{ width: '100vw', height: '100dvh', overflow: 'hidden' }}>
      {/* Remote Video (Full Screen) */}
      <div className="flex-1 relative bg-black" onClick={() => {
        // Tap to play if autoplay was blocked on mobile
        if (remoteVideoRef.current && remoteVideoRef.current.paused) {
          remoteVideoRef.current.play().catch(() => {});
        }
      }}>
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="absolute inset-0 w-full h-full bg-black"
          style={{ objectFit: 'contain', maxWidth: '100%', maxHeight: '100%' }}
        />
        
        {/* Name on top - always visible */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/60 to-transparent z-10">
          <h2 className="text-white text-xl font-semibold text-center" data-testid="call-remote-user-name">
            {remoteUser?.display_name || 'Unknown'}
          </h2>
          <p className="text-white/60 text-sm text-center mt-0.5" data-testid="call-status-text">
            {callStatus === 'connected' 
              ? (currentCallType === 'audio' ? 'Audio Call' : 'Video Call')
              : callStatus === 'connecting' ? 'Connecting...' 
              : callStatus === 'ringing' ? 'Ringing...' 
              : 'Calling...'}
          </p>
        </div>

        {/* Profile pic in center - shown when not connected OR audio call */}
        {(callStatus !== 'connected' || currentCallType === 'audio') && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900/90 to-black/90">
            <div className="text-center">
              {remoteUser?.avatar_url ? (
                <img 
                  src={remoteUser.avatar_url.startsWith('http') ? remoteUser.avatar_url : `${BACKEND_URL}${remoteUser.avatar_url}`}
                  alt={remoteUser.display_name}
                  className="w-36 h-36 rounded-full object-cover mx-auto mb-4 border-4 border-[#D4AF37]/30 shadow-2xl"
                  data-testid="call-remote-user-avatar"
                />
              ) : (
                <div className="w-36 h-36 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium text-5xl mx-auto mb-4 shadow-2xl" data-testid="call-remote-user-avatar">
                  {remoteUser?.display_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
              {callStatus !== 'connected' && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="w-2 h-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Local Video (Picture in Picture) */}
        <div className="absolute top-4 right-4 w-28 h-40 sm:w-32 sm:h-48 rounded-2xl overflow-hidden bg-gray-800 shadow-lg border border-gray-700">
          <video
            ref={localVideoCallbackRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full bg-gray-800"
            style={{ objectFit: 'cover' }}
          />
          {!isVideoEnabled && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <VideoOff size={24} className="text-gray-500" />
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-gray-800/90">
        <div className="flex items-center justify-center gap-3">
          <button
            data-testid="toggle-audio-btn"
            onClick={toggleAudio}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              isAudioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {isAudioEnabled ? (
              <Mic size={20} className="text-white" />
            ) : (
              <MicOff size={20} className="text-white" />
            )}
          </button>

          {/* Speaker toggle */}
          <button
            data-testid="toggle-speaker-btn"
            onClick={toggleSpeaker}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              isSpeakerOn ? 'bg-[#D4AF37] hover:bg-[#C5A059]' : 'bg-gray-700 hover:bg-gray-600'
            }`}
          >
            <Volume2 size={20} className="text-white" />
          </button>

          <button
            data-testid="end-call-btn"
            onClick={endCall}
            className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors"
          >
            <PhoneOff size={24} className="text-white" />
          </button>

          {currentCallType === 'video' ? (
            <button
              data-testid="toggle-video-btn"
              onClick={toggleVideo}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                isVideoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              {isVideoEnabled ? (
                <Video size={20} className="text-white" />
              ) : (
                <VideoOff size={20} className="text-white" />
              )}
            </button>
          ) : (
            <button
              data-testid="switch-to-video-btn"
              onClick={switchToVideo}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors"
            >
              <Video size={20} className="text-white" />
            </button>
          )}

          {/* Add People button */}
          <button
            data-testid="add-people-to-call-btn"
            onClick={async () => {
              setShowAddPeople(!showAddPeople);
              if (!showAddPeople && contacts.length === 0) {
                try {
                  const res = await axios.get(`${API}/users/search`, {
                    params: { query: '' },
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  const otherUsers = (res.data || []).filter(u => u.id !== user?.id && u.id !== remoteUser?.id);
                  setContacts(otherUsers);
                } catch (e) {
                  console.error('Failed to fetch contacts:', e);
                }
              }
            }}
            className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors"
          >
            <UserPlus size={20} className="text-white" />
          </button>
        </div>
      </div>

      {/* Add People Panel */}
      {showAddPeople && (
        <div className="absolute bottom-28 left-0 right-0 mx-4 max-h-60 bg-gray-800 rounded-xl border border-gray-700 shadow-2xl overflow-y-auto z-20" data-testid="add-people-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
            <span className="text-white text-sm font-medium">Add people to call</span>
            <button onClick={() => setShowAddPeople(false)} className="text-gray-400 hover:text-white">
              <X size={16} />
            </button>
          </div>
          {contacts.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No contacts available</p>
          ) : (
            contacts.map(contact => {
              const isInvited = invitedUsers.includes(contact.id);
              return (
                <button
                  key={contact.id}
                  disabled={isInvited}
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/calls/initiate`, {
                        to_user_id: contact.id,
                        call_type: currentCallType
                      }, { headers: { Authorization: `Bearer ${token}` } });
                      setInvitedUsers(prev => [...prev, contact.id]);
                      toast.success(`Calling ${contact.display_name}...`);
                    } catch {
                      toast.error('Failed to add to call');
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-700/50 transition-colors ${isInvited ? 'opacity-50' : ''}`}
                  data-testid={`invite-${contact.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                    {contact.display_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <span className="text-white text-sm flex-1 text-left">{contact.display_name}</span>
                  {isInvited && <span className="text-green-400 text-xs">Invited</span>}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default VideoCall;
